import { MannerOfArticulation, PlaceOfArticulation, Voicing } from "../phonemes/features";
import { IpaType, ipaToFeatures } from "../phonemes/ipa";

/**
 * find the closest consonant to 'consonant' from within 'checkConsonants'
 * based on attributes such as voicing, place & manner of articulation
 */
export function closestConsonant(consonant: IpaType, checkConsonants: IpaType[]): IpaType | null {
  if (checkConsonants.includes(consonant)) {
    // if it's in the list, it's trivially the closest
    return consonant;
  }
  const features = ipaToFeatures(consonant);
  if (!features) {
    return null;  // unrecognized symbols
  } else if (!features.includes('consonant')) {
    return null;  // must be a consonant
  }

  return getClosestByFeatures(consonant, checkConsonants);
}

/** compare features to pick the closest sound */
function getClosestByFeatures(consonant: IpaType, checkConsonants: IpaType[]): IpaType | null {
  const inputDetails = getDetails(consonant);
  const checkDetails = checkConsonants.map(ipa => getDetails(ipa));

  let closestDistance = Number.MAX_VALUE;
  let bestConsonant = null;
  checkDetails.forEach((current, i) => {
    const thisDistance = computeDetailDistance(inputDetails, current);
    if (thisDistance !== 'mismatch' && thisDistance < closestDistance) {
      closestDistance = thisDistance;
      bestConsonant = checkConsonants[i];
    }
  });

  return bestConsonant;
}

/** common features of consonants */
interface Details {
  place?: PlaceOfArticulation;
  manner?: MannerOfArticulation;
  voicing?: Voicing;
}

/** get place/manner of articulation + voicing */
function getDetails(consonant: IpaType): Details {
  const details: Details = {};
  const features = ipaToFeatures(consonant);
  features?.forEach(f => {
    if (placeOrder.includes(f as PlaceOfArticulation)) {
      details.place = f as PlaceOfArticulation;
    }
    if (mannersOrder.includes(f as MannerOfArticulation)) {
      details.manner = f as MannerOfArticulation;
    }
    if (allVoicings.includes(f as Voicing)) {
      details.voicing = f as Voicing;
    }
  });
  return details;
}

/** ranging from the front of the mouth to the back of the mouth */
const placeOrder: PlaceOfArticulation[] = [
  'bilabial', 'labiodental', 'labial-velar',
  'dental', 'alveolar', 'postalveolar', 'retroflex',
  'alveolo-palatal', 'palatal',
  'velar', 'uvular', 'pharyngeal', 'epiglottal', 'glottal'
];

/** rough ordering for the manner of articulation */
const mannersOrder: MannerOfArticulation[] = [
  'plosive', 'nasal', 'trill', 'tap-flap', 'lateral-flap', 'affricate',
  'fricative', 'lateral-fricative', 'approximant', 'lateral-approximant']

const allVoicings: Voicing[] = ['voiceless', 'voiced']

/** compute a theoretical distance between two sounds with 'place' & 'manner' */
function computeDetailDistance(a: Details, b: Details): number | 'mismatch' {
  let place = getPlaceDistance(a.place, b.place);
  let manner = getMannerDistance(a.manner, b.manner);
  let voice = getVoiceDistance(a.voicing, b.voicing);

  if (place === 'mismatch' || manner === 'mismatch') {
    // this function requires 'place' & 'manner' to do its work
    return 'mismatch';
  }
  if (voice === 'mismatch') {
    // if only one is marked w/ voicing info, consider them close
    voice = 1;
  }

  // make changing place more likely than manner
  place = Math.abs(place);
  // lenition (weakening) is more likely than fortition (strengthening)
  manner = manner > 0 ? 1.2 * manner : -1.4 * manner;
  // make a change in voicing in between place & manner
  voice = Math.abs(1.1 * voice);

  return place + manner + voice;
}

function getPlaceDistance(a?: PlaceOfArticulation, b?: PlaceOfArticulation): number | 'mismatch' {
  return getDistance(placeOrder, a, b);
}
function getMannerDistance(a?: MannerOfArticulation, b?: MannerOfArticulation): number | 'mismatch' {
  return getDistance(mannersOrder, a, b);
}
function getVoiceDistance(a?: Voicing, b?: Voicing): number | 'mismatch' {
  return getDistance(allVoicings, a, b);
}

/**
 * get the distance from a to b if they're in 'options' (possibly <0),
 * if both undefined, return 0
 * if one isn't in 'options', return 'mismatch'
 */
function getDistance(options: string[], a?: string, b?: string): number | 'mismatch' {
  if (a === b) {
    // both the same, could both be undefined
    return 0;
  }
  const aDistance = getIndex(options, a);
  const bDistance = getIndex(options, b);
  if (aDistance === null || bDistance === null) {
    return 'mismatch';
  }
  return bDistance - aDistance;
}

/** if 'value' is in 'options', return the index, else null */
function getIndex(options: string[], value?: string): number | null {
  if (!value) {
    return null;
  }
  const i = options.indexOf(value);
  return i === -1 ? null : i;
}
